import type { CachedCredentialDto } from '@terra/shared-contracts-authenticator';
import type { MtlsEntry } from '../electron/proxy-types';
import { buildBrowserInjectionPlan } from '../components/layout/browserLaunchReadiness';
import { fetchApplicationCredentialById } from './fetchApplicationCredentialById';
import { getCurrentGatewayUrl } from './environmentDetection';
import { getAuthHeaders } from './authHeaders';
import { cachedCredentialsClient } from '@terra/shared-rest-clients-authenticator';

async function fetchCachedCredentialById({
  applicationId,
  credentialId,
}: {
  applicationId: string;
  credentialId: string;
}): Promise<CachedCredentialDto | undefined> {
  const authHeaders = await getAuthHeaders();
  const response = await cachedCredentialsClient.getCachedCredentials({
    params: { applicationId },
    overrideClientOptions: {
      baseUrl: `${getCurrentGatewayUrl()}/ai`,
    },
    extraHeaders: authHeaders,
  });

  if (response.status !== 200) {
    return undefined;
  }

  return response.body.credentials.find((entry) => entry.id === credentialId);
}

export async function injectProxiedBrowserCredentials({
  browserId,
  credentialId,
  applicationId,
  applicationUrl,
  startUrl,
}: {
  browserId: string;
  credentialId: string;
  applicationId: string;
  applicationUrl?: string;
  startUrl?: string;
}): Promise<{ success: boolean; error?: string }> {
  if (typeof window === 'undefined' || !window.electronAPI) {
    return { success: false, error: 'Electron API is not available' };
  }

  const credential = await fetchApplicationCredentialById({
    applicationId,
    credentialId,
  });

  if (!credential) {
    return {
      success: false,
      error: `Credential ${credentialId} not found for application`,
    };
  }

  const cached = await fetchCachedCredentialById({
    applicationId,
    credentialId,
  });

  const credentialTargetUrl = credential.loginUrl ?? applicationUrl;
  const hasCredentialMtls = Boolean(
    credential.mtlsCert || credential.mtlsKey || credential.mtlsPfx,
  );
  const credentialMtlsOrigin = credential.mtlsOrigin ?? credentialTargetUrl;

  if (hasCredentialMtls && credentialMtlsOrigin) {
    const entry: MtlsEntry = {
      id: credential.id,
      origin: credentialMtlsOrigin,
      cert: credential.mtlsCert ?? null,
      key: credential.mtlsKey ?? null,
      pfx: credential.mtlsPfx ?? null,
      passphrase: credential.mtlsPassphrase ?? null,
      source: 'instance',
      enabled: true,
    };
    const bindResult = await window.electronAPI.proxy.bindInstanceMtls({
      instanceId: browserId,
      entry,
    });
    if (!bindResult.success) {
      return {
        success: false,
        error: bindResult.error || 'Failed to bind credential mTLS to browser',
      };
    }
  }

  const injectionPlan = buildBrowserInjectionPlan({
    credential,
    cached,
    applicationUrl,
    credentialTargetUrl,
  });

  const injectResult = await window.electronAPI.browser.injectCredentials({
    browserId,
    targetUrl: startUrl ?? injectionPlan.targetUrl,
    credentials: injectionPlan.credentials,
  });

  if (!injectResult.success) {
    return {
      success: false,
      error: injectResult.error || 'Failed to inject credentials into browser',
    };
  }

  return { success: true };
}
